import { API_BASE_URL } from '../utils/constants';
import axiosInstance from './axiosInstance';
import indexedDBService from './IndexedDBService';
import { toBackendStatus } from '../utils/statusTransitions';

export const DocumentService = {
    // Fetch documents for the authenticated user
    fetchDocuments: async (options = {}) => {
        try {
            const { limit } = options || {};
            const response = await axiosInstance.get(`${API_BASE_URL}/document`, {
                params: typeof limit === 'number' ? { limit } : undefined,
            });
            return response.data;
        }
        catch (error) {
            throw error;
        }
    },
    // Fetch document details using document ID with IndexedDB integration
    fetchDocumentById: async (documentId) => {
        try {
            // Fetch from API
            const response = await axiosInstance.get(`${API_BASE_URL}/document/${documentId}`);
            const apiDocument = response.data;

            // Replace the current document in IndexedDB with the new one
            // This ensures we always have the latest version from the server
            if (apiDocument?.document) {
                await indexedDBService.replaceCurrentDocument(apiDocument.document);
            }

            return apiDocument;
        }
        catch (error) {
            // If API fails, try to return cached version from IndexedDB
            try {
                const cachedDocument = await indexedDBService.getDocument(documentId);
                if (cachedDocument) {
                    console.warn('Using cached document due to API error:', error.message);
                    // When falling back to cache, we do not have entitlement info
                    return {
                        document: cachedDocument,
                        exportEntitlements: null
                    };
                }
            } catch (cacheError) {
                console.error('Failed to retrieve cached document:', cacheError);
            }

            throw error;
        }
    },
    // Fetch available formats
    fetchFormats: async () => {
        try {
            const response = await axiosInstance.get(`${API_BASE_URL}/format`);
            return response.data;
        }
        catch (error) {
            throw error;
        }
    },

    //fetch document types
    fetchDocumentTypes: async () => {
        try {
            const response = await axiosInstance.get(`${API_BASE_URL}/document-type`);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Search universities by format and document type
    searchUniversities: async ({ formatId, documentTypeId }) => {
        try {
            const response = await axiosInstance.get(`${API_BASE_URL}/university/search`, {
                params: { formatId, documentTypeId }
            });
            return response.data;
        }
        catch (error) {
            throw error;
        }
    },
    // Create a new document
    createDocument: async (payload) => {
        try {
            const response = await axiosInstance.post(`${API_BASE_URL}/document`, payload);
            return response.data;
        }
        catch (error) {
            throw error;
        }
    },

    // Create a document from an uploaded .docx file (import flow)
    importDocument: async (file, payload) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', payload.title || 'Untitled Document');
            formData.append('format', payload.format);
            formData.append('documentType', payload.documentType);
            formData.append('university', payload.university ?? 'others');
            formData.append('status', payload.status || 'Draft');
            // Do not set Content-Type manually — the browser must add the multipart boundary.
            const response = await axiosInstance.post(`${API_BASE_URL}/document/import`, formData, {
                timeout: 180000,
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    //rename a document
    renameDocument: async (documentId, newTitle) => {
        try {
            const response = await axiosInstance.post(
                `${API_BASE_URL}/document/${documentId}/rename`,
                { title: newTitle }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Delete a document
    deleteDocument: async (documentId) => {
        try {
            const response = await axiosInstance.delete(
                `${API_BASE_URL}/document/${documentId}`
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Update document status (UI labels Review/Final/Submitted mapped server-side too)
    updateDocumentStatus: async (documentId, status, documentType = '') => {
        try {
            const response = await axiosInstance.patch(
                `${API_BASE_URL}/document/${documentId}/status`,
                { status: toBackendStatus(status, documentType) }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    /** Partial document update (title, status, finalFormatSettings, etc.) */
    updateDocument: async (documentId, body) => {
        try {
            const response = await axiosInstance.put(
                `${API_BASE_URL}/document/${documentId}`,
                body
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // duplicate a document
    duplicateDocument: async (documentId) => {
        try {
            const response = await axiosInstance.post(
                `${API_BASE_URL}/document/${documentId}/duplicate`
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Update document fileContent in IndexedDB only
    updateDocumentLocalFileContent: async (documentId, fileContent) => {
        try {
            console.log('Updating local document AAHAHAHD:', fileContent);
            await indexedDBService.updateDocumentContent(documentId, fileContent);
            return { success: true, message: 'Document fileContent updated locally' };
        }
        catch (error) {
            throw error;
        }
    },

    // Update local metadata (title, etc.) without replacing the whole cache entry
    updateDocumentLocalMetadata: async (documentId, metadata) => {
        try {
            await indexedDBService.updateDocumentMetadata(documentId, metadata);
            return { success: true };
        } catch (error) {
            // Non-fatal when cache is empty; next fetch will repopulate
            console.warn('Local metadata update skipped:', error?.message || error);
            return { success: false };
        }
    },

    // Push latest fileContent from IndexedDB to server
    syncDocumentFileContent: async (documentId) => {
        try {
            const cached = await indexedDBService.getDocument(documentId);
            const fileContent = cached?.fileContent;
            console.log('Syncing document fileContent to server for documentId:', documentId);
            console.log('fileContent :', fileContent);
            if (!fileContent) {
                return { success: false, message: 'No fileContent found in cache' };
            }
            const response = await axiosInstance.put(
                `${API_BASE_URL}/document/${documentId}/content`,
                { fileContent }
            );
            // On success, update lastSynced
            await indexedDBService.updateLastSynced(documentId);
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Upload document image
    uploadDocumentImage: async (documentId, formData, onUploadProgress) => {
        try {
            // Let Axios/Browser set the Content-Type with boundary for FormData
            const response = await axiosInstance.post(
                `${API_BASE_URL}/document/${documentId}/upload-image`,
                formData,
                {
                    onUploadProgress: (progressEvent) => {
                        if (onUploadProgress && progressEvent.total) {
                            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            onUploadProgress(percentCompleted);
                        }
                    }
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Delete document images
    deleteDocumentImages: async (documentId, imageUrls) => {
        try {
            const response = await axiosInstance.delete(
                `${API_BASE_URL}/document/${documentId}/images`,
                { data: { imageUrls } }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    },

    // Get document from IndexedDB only (for offline access)
    getCachedDocument: async (documentId) => {
        try {
            return await indexedDBService.getDocument(documentId);
        }
        catch (error) {
            throw error;
        }
    },

    // Get last synced timestamp for a document
    getLastSyncedFromIdb: async (documentId) => {
        try {
            return await indexedDBService.getLastSynced(documentId);
        }
        catch (error) {
            throw error;
        }
    },

    // Update last synced timestamp
    updateLastSyncedInIdb: async (documentId, timestamp = null) => {
        try {
            return await indexedDBService.updateLastSynced(documentId, timestamp);
        }
        catch (error) {
            throw error;
        }
    },
}